// Turn search rules into highlights over a PDF.js text layer.
//
// A page's text arrives as separate items (often one word, sometimes a glyph
// fragment). A phrase like "effective width" is therefore rarely one string, and
// typeset "ff" is often the single ligature U+FB00, so a per-item regex misses
// it. We join items the way a reader sees them, fold ligatures and typographic
// punctuation back to plain characters, then map each match onto the items that
// own those characters.

const CHAR_FOLD = {
  '\u2010': '-',
  '\u2011': '-',
  '\u2018': "'",
  '\u2019': "'",
  '\u201A': "'",
  '\u201B': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u201E': '"',
  '\u201F': '"',
  '\u00A0': ' ',
}

function escapeHtml(text) {
  return text.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
}

function foldChar(ch) {
  if (CHAR_FOLD[ch]) return CHAR_FOLD[ch]
  const folded = ch.normalize('NFKC')
  return folded || ch
}

function foldString(text) {
  let out = ''
  for (const ch of text) out += foldChar(ch)
  return out
}

function isWordChar(ch) {
  return /\w/.test(ch)
}

// Fold ligatures and quotes, and drop a hyphen that exists only because a word
// was broken across a line. `origin[i]` is the index in `text` of normalized
// character i.
function normalizeForMatch(text) {
  const folded = []
  const foldedOrigin = []
  for (let i = 0; i < text.length; i += 1) {
    const rep = foldChar(text[i])
    for (let j = 0; j < rep.length; j += 1) {
      folded.push(rep[j])
      foldedOrigin.push(i)
    }
  }

  let out = ''
  const origin = []
  let i = 0
  while (i < folded.length) {
    const prev = out.length ? out[out.length - 1] : ''
    if (folded[i] === '-' && isWordChar(prev)) {
      let j = i + 1
      while (folded[j] === ' ' || folded[j] === '\t') j += 1
      if (folded[j] === '\n') {
        j += 1
        while (folded[j] === ' ' || folded[j] === '\t') j += 1
        if (isWordChar(folded[j])) {
          i = j
          continue
        }
      }
    }
    out += folded[i]
    origin.push(foldedOrigin[i])
    i += 1
  }
  return { text: out, origin }
}

function originalSpan(origin, start, end) {
  if (start >= end || start >= origin.length) return null
  const last = Math.min(end, origin.length) - 1
  return [origin[start], origin[last] + 1]
}

export function compileHighlightRules(rules) {
  return (rules || [])
    .map((rule) => {
      const text = foldString(String(rule?.text ?? '').trim())
      if (!text) return null
      let source = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')
      if (rule?.wholeWords) source = `\\b${source}\\b`
      return new RegExp(source, rule?.caseSensitive ? 'g' : 'gi')
    })
    .filter(Boolean)
}

function fontSizeOf(item) {
  const t = item?.transform || []
  return Math.max(Math.abs(t[0] || 0), Math.abs(t[3] || 0), item?.height || 0, 1)
}

// A space or newline that is visible on the page but not stored in either item.
function separatorBetween(prev, next) {
  if (!prev?.str || !next?.str) return ''
  if (/\s$/.test(prev.str) || /^\s/.test(next.str)) return ''
  if (prev.hasEOL) return '\n'
  const font = Math.max(fontSizeOf(prev), fontSizeOf(next))
  const ay = prev.transform?.[5]
  const by = next.transform?.[5]
  if (ay == null || by == null || Math.abs(ay - by) > font * 0.5) return '\n'
  const prevEnd = (prev.transform?.[4] || 0) + (prev.width || 0)
  const gap = (next.transform?.[4] || 0) - prevEnd
  if (gap > font * 0.15) return ' '
  return ''
}

function indexPageItems(items) {
  let text = ''
  const spans = []
  let prev = null
  ;(items || []).forEach((item, itemIndex) => {
    if (typeof item?.str !== 'string' || !item.str) return
    text += separatorBetween(prev, item)
    const start = text.length
    text += item.str
    spans.push({ itemIndex, start, end: text.length })
    prev = item
  })
  return { text, spans }
}

function mergeRanges(ranges) {
  if (!ranges.length) return []
  const sorted = ranges.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const merged = [sorted[0].slice()]
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1]
    const [s, e] = sorted[i]
    if (s <= last[1]) last[1] = Math.max(last[1], e)
    else merged.push([s, e])
  }
  return merged
}

function matchRanges(text, origin, regexes) {
  const ranges = []
  for (const rx of regexes) {
    rx.lastIndex = 0
    let m
    while ((m = rx.exec(text)) !== null) {
      if (m[0].length === 0) {
        rx.lastIndex += 1
        continue
      }
      const span = originalSpan(origin, m.index, m.index + m[0].length)
      if (span) ranges.push(span)
    }
  }
  return mergeRanges(ranges)
}

export function markRanges(str, ranges) {
  if (!ranges?.length) return escapeHtml(str)
  let out = ''
  let cursor = 0
  for (const range of ranges) {
    const s = Math.max(0, Math.min(str.length, range[0]))
    const e = Math.max(s, Math.min(str.length, range[1]))
    if (s < cursor) continue
    out += escapeHtml(str.slice(cursor, s))
    out += `<mark class="pdfviewer-hl">${escapeHtml(str.slice(s, e))}</mark>`
    cursor = e
  }
  out += escapeHtml(str.slice(cursor))
  return out
}

// Highlight one text item on its own. Used until the page's items are indexed.
export function highlightString(str, regexes) {
  if (!regexes?.length) return escapeHtml(str)
  const { text, origin } = normalizeForMatch(str)
  return markRanges(str, matchRanges(text, origin, regexes))
}

// itemIndex -> [start, end) ranges inside that item's `str`.
export function rangesByItem(items, regexes) {
  if (!regexes?.length || !items?.length) return {}
  const { text, spans } = indexPageItems(items)
  const { text: normalized, origin } = normalizeForMatch(text)
  const joinedRanges = matchRanges(normalized, origin, regexes)
  if (!joinedRanges.length) return {}

  const byItem = {}
  let si = 0
  for (const [ms, me] of joinedRanges) {
    while (si < spans.length && spans[si].end <= ms) si += 1
    for (let j = si; j < spans.length && spans[j].start < me; j += 1) {
      const span = spans[j]
      const s = Math.max(span.start, ms) - span.start
      const e = Math.min(span.end, me) - span.start
      if (s < e) {
        if (!byItem[span.itemIndex]) byItem[span.itemIndex] = []
        byItem[span.itemIndex].push([s, e])
      }
    }
  }
  for (const key of Object.keys(byItem)) byItem[key] = mergeRanges(byItem[key])
  return byItem
}
